import { CloseOutlined } from '@mui/icons-material';
import { Button } from 'finara-components';
import React, { useState, useEffect } from 'react';

import { Input } from '@/components/common/Input';
import type { User, UpdateUserData } from '@/types/user';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateUserData) => void;
  user: User | null;
  isLoading?: boolean;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<UpdateUserData>({
    name: '',
    email: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
      });
    }
  }, [user]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.name && !formData.name.trim()) {
      newErrors['name'] = 'El nombre no puede estar vacío';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors['email'] = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData: UpdateUserData = {};

    if (formData.name && formData.name !== user?.name) {
      submitData.name = formData.name;
    }

    if (formData.email && formData.email !== user?.email) {
      submitData.email = formData.email;
    }

    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({ name: '', email: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black opacity-60 cursor-default"
        onClick={handleClose}
        aria-label="Cerrar modal"
        type="button"
      />

      {/* Modal */}
      <div className="relative z-10 bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-brand-white">Editar Usuario</h2>
          <button
            onClick={handleClose}
            className="p-2 text-muted hover:text-brand-white transition-colors"
            disabled={isLoading}
            type="button"
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Input
              id="name"
              label="Nombre Completo"
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: Juan Pérez García"
            />
            {errors['name'] && <p className="text-sm text-red-400 mt-1">{errors['name']}</p>}
          </div>

          <div>
            <Input
              id="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Ej: juan.perez@empresa.com"
            />
            {errors['email'] && <p className="text-sm text-red-400 mt-1">{errors['email']}</p>}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button variant="white" onClick={handleClose} disabled={isLoading} type="button">
              Cancelar
            </Button>
            <Button type="submit" variant="black" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
